import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  Loader2,
  Mic,
  Plus,
  Send,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
  Volume2,
  X,
} from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/cn'
import { sanitize } from '@/lib/html'
import { useAgents } from '@/features/agents/queries'
import { useWorkflows, useWorkflowAction } from '@/features/workflows/queries'
import { useProfile } from '@/features/profile/queries'
import { useQueryClient } from '@tanstack/react-query'
import { useTenant } from '@/hooks/useTenant'
import {
  useChatMessageFeedback,
  useChatThreadMessages,
  useThreadFeedback,
} from '@/features/chatbot/queries'
import { renderChatMarkdown } from '@/features/chatbot/markdown'
import { ThreadSidebar } from '@/features/chatbot/ThreadSidebar'
import { ChatHeader } from '@/features/chatbot/ChatHeader'
import { VoiceRecorderOverlay } from '@/features/chatbot/VoiceRecorderOverlay'
import { PartsRenderer } from '@/features/chatbot/renderers/PartsRenderer'
import { CitationList } from '@/features/chatbot/renderers/CitationList'
import { streamChat } from '@/features/chatbot/stream'
import { useSpeechRecognition } from '@/features/chatbot/useSpeechRecognition'
import { useSpeech } from '@/features/chatbot/useSpeech'
import { SpeakingWaves } from '@/features/chatbot/SpeakingWaves'
import type { ChatCitation, ChatMessagePart } from '@/features/chatbot/types'
import type { Agent } from '@/features/agents/types'
import type { Workflow } from '@/features/workflows/types'

type LocalMessage =
  | { id: string; role: 'user'; text: string; messageId?: string }
  | {
      id: string
      role: 'assistant'
      html: string
      text?: string
      messageId?: string
      agentName?: string
      parts?: ChatMessagePart[] | null
      citations?: ChatCitation[] | null
    }
  | { id: string; role: 'status'; text: string }

function hasTextSegments(parts: ChatMessagePart[] | null | undefined): boolean {
  return Array.isArray(parts) && parts.some((p) => p.kind === 'text')
}

function timeGreeting(): string {
  const h = new Date().getHours()
  if (h < 12) return 'Good morning'
  if (h < 17) return 'Good afternoon'
  return 'Good evening'
}

function initialsFor(name: string): string {
  const trimmed = (name || '?').trim()
  if (!trimmed) return '?'
  const parts = trimmed.split(/\s+/)
  const first = parts[0] ?? '?'
  if (parts.length === 1) return first.slice(0, 2).toUpperCase()
  const second = parts[1] ?? ''
  return ((first[0] ?? '') + (second[0] ?? '')).toUpperCase()
}

export function ChatbotPage() {
  const profileQ = useProfile()
  const agentsQ = useAgents()
  const workflowsQ = useWorkflows()
  const feedbackM = useChatMessageFeedback()
  const workflowAction = useWorkflowAction()
  const qc = useQueryClient()
  const tenantId = useTenant()
  const [params] = useSearchParams()

  const [activeThreadId, setActiveThreadId] = useState<string | null>(null)
  const [input, setInput] = useState('')
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null)
  const [liveMessages, setLiveMessages] = useState<LocalMessage[]>([])
  const [streaming, setStreaming] = useState(false)
  // Visibility queued by the "+" picker. Carried until the first message
  // is sent - the create-on-stream branch uses it to set the new thread's
  // visibility from the start (and notify the audience). Cleared once
  // the thread is born so subsequent New chats default to private again.
  const [pendingVisibility, setPendingVisibility] = useState<{
    visibility: 'private' | 'tenant' | 'shared'
    sharedRoleIds: number[]
    sharedUserIds: number[]
  } | null>(null)
  const [voiceOverlayOpen, setVoiceOverlayOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem('bokap.chat.sidebarCollapsed') === '1' } catch { return false }
  })

  const speech = useSpeech()
  const voiceInput = useSpeechRecognition({
    onFinal: () => {
      // The overlay reads transcript/interim directly from the hook - no need
      // to mirror into `input` during listening. The user confirms with
      // "Use text" which calls setInput explicitly.
    },
  })
  const [toolsOpen, setToolsOpen] = useState(false)
  const [toolsTab, setToolsTab] = useState<'agents' | 'workflows'>('agents')
  const [toolsQuery, setToolsQuery] = useState('')
  const [feedbackLocal, setFeedbackLocal] = useState<Record<string, 1 | -1 | 0>>({})

  const messagesEndRef = useRef<HTMLDivElement | null>(null)
  const preselectedRef = useRef(false)
  // First-message path: when the stream completes it hands us a brand-new
  // thread id. Setting that thread id used to wipe the live buffer here, which
  // blanked the chat for the frame between "stream done" and "persisted query
  // returned" - the page snapped back to the greeting and then snapped to the
  // saved messages, reading as a flash. Flip this ref before the internal
  // setActiveThreadId so the effect skips the clear once; any real thread
  // switch (sidebar click, etc.) still clears as before.
  const skipClearOnNextThreadChange = useRef(false)

  const threadQ = useChatThreadMessages(activeThreadId)
  const feedbackQ = useThreadFeedback(activeThreadId)

  useEffect(() => {
    if (skipClearOnNextThreadChange.current) {
      skipClearOnNextThreadChange.current = false
      return
    }
    setLiveMessages([])
    setFeedbackLocal({})
  }, [activeThreadId])

  // Hydrate thumbs state from the server for the current thread so the
  // UI remembers my rating across refreshes and across members (is_mine
  // gates which row wins).
  useEffect(() => {
    const rows = feedbackQ.data?.feedback
    if (!rows) return
    setFeedbackLocal((prev) => {
      const next = { ...prev }
      for (const f of rows) {
        if (f.is_mine && (f.rating === 1 || f.rating === -1) && !(f.message_id in prev)) {
          next[f.message_id] = f.rating
        }
      }
      return next
    })
  }, [feedbackQ.data])

  const persistedMessages = useMemo<LocalMessage[]>(() => {
    const rows = threadQ.data?.messages ?? []
    return rows.map((m): LocalMessage => {
      if (m.role === 'user') return { id: m.id, role: 'user', text: m.content }
      return {
        id: m.id,
        role: 'assistant',
        html: sanitize(renderChatMarkdown(m.content || '')),
        text: m.content || '',
        messageId: m.id,
        parts: (m.parts as ChatMessagePart[] | null | undefined) ?? null,
        citations: (m.citations as ChatCitation[] | null | undefined) ?? null,
      }
    })
  }, [threadQ.data])

  const messages = useMemo<LocalMessage[]>(() => {
    const persistedIds = new Set(persistedMessages.map((m) => m.id))
    const filteredLive = liveMessages.filter((m) => {
      if (m.role === 'assistant' && m.messageId && persistedIds.has(m.messageId)) return false
      if (
        m.role === 'user' &&
        'messageId' in m &&
        (m as { messageId?: string }).messageId &&
        persistedIds.has((m as { messageId?: string }).messageId!)
      )
        return false
      return true
    })
    return [...persistedMessages, ...filteredLive]
  }, [persistedMessages, liveMessages])

  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev
      try { localStorage.setItem('bokap.chat.sidebarCollapsed', next ? '1' : '0') } catch { /* ignore */ }
      return next
    })
  }

  const started = messages.length > 0 || streaming
  const firstName = useMemo(() => {
    const n = profileQ.data?.name ?? ''
    return n ? (n.split(/\s+/)[0] ?? '') : ''
  }, [profileQ.data?.name])

  const greeting = firstName ? `${timeGreeting()}, ${firstName}.` : `${timeGreeting()}.`

  useEffect(() => {
    if (preselectedRef.current) return
    const requested = params.get('agent_id')
    if (!requested) return
    const list = agentsQ.data?.agents ?? []
    const match = list.find((a) => a.id === requested)
    if (match) {
      setSelectedAgent(match)
      preselectedRef.current = true
    }
  }, [params, agentsQ.data])

  // Open a specific thread when arriving with `?thread=<id>` - used by
  // the BackToChatBanner that resource-detail pages render when the
  // user came in via a citation. Runs once per id so user-driven thread
  // switches inside the page aren't fought by the URL param.
  const preselectedThreadRef = useRef<string | null>(null)
  useEffect(() => {
    const requested = params.get('thread')
    if (!requested) return
    if (preselectedThreadRef.current === requested) return
    preselectedThreadRef.current = requested
    setActiveThreadId(requested)
  }, [params])

  useEffect(() => {
    if (started && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages.length, started])

  const filteredAgents = useMemo(() => {
    const q = toolsQuery.trim().toLowerCase()
    const list = agentsQ.data?.agents ?? []
    if (!q) return list
    return list.filter(
      (a) =>
        a.name.toLowerCase().includes(q) ||
        (a.description ?? '').toLowerCase().includes(q),
    )
  }, [agentsQ.data, toolsQuery])

  const filteredWorkflows = useMemo(() => {
    const q = toolsQuery.trim().toLowerCase()
    const list = workflowsQ.data?.workflows ?? []
    if (!q) return list
    return list.filter(
      (w) =>
        w.name.toLowerCase().includes(q) ||
        (w.description ?? '').toLowerCase().includes(q),
    )
  }, [workflowsQ.data, toolsQuery])

  const openTools = () => {
    setToolsQuery('')
    setToolsOpen(true)
  }

  const chooseAgent = (a: Agent) => {
    setSelectedAgent(a)
    setToolsOpen(false)
  }

  const clearAgent = () => setSelectedAgent(null)

  const pushLive = (m: LocalMessage) => setLiveMessages((prev) => [...prev, m])

  const openVoiceOverlay = () => {
    voiceInput.reset()
    setVoiceOverlayOpen(true)
    voiceInput.start()
  }
  const stopVoice = () => {
    voiceInput.stop()
  }
  const resumeVoice = () => {
    // Resume without clearing the transcript - continues the same session.
    if (!voiceInput.listening) voiceInput.start()
  }
  const restartVoice = () => {
    // Discard what we have and start fresh.
    if (voiceInput.listening) voiceInput.stop()
    voiceInput.reset()
    // Small defer so stop() completes before start() - some browsers throw
    // InvalidStateError if start() fires while a previous session is still
    // shutting down.
    window.setTimeout(() => voiceInput.start(), 50)
  }
  const cancelVoice = () => {
    voiceInput.stop()
    voiceInput.reset()
    setVoiceOverlayOpen(false)
  }
  const useVoiceText = (text: string) => {
    if (text) {
      setInput((prev) => {
        const existing = prev.trim()
        return existing ? `${existing} ${text}` : text
      })
    }
    voiceInput.reset()
    setVoiceOverlayOpen(false)
  }

  const runWorkflow = async (wf: Workflow) => {
    setToolsOpen(false)
    pushLive({
      id: `status-${Date.now()}`,
      role: 'status',
      text: `Triggering workflow ${wf.name}…`,
    })
    try {
      await workflowAction.mutateAsync({ id: wf.id, kind: 'run' })
      pushLive({
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        html: sanitize(
          renderChatMarkdown(
            `Workflow **${wf.name}** is running. You can follow it on the [Workflows page](/workflows).`,
          ),
        ),
      })
    } catch {
      pushLive({
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        html: sanitize(renderChatMarkdown("Couldn't start the workflow.")),
      })
    }
  }

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const text = input.trim()
    if (!text || streaming) return
    if (voiceInput.listening) voiceInput.stop()
    voiceInput.reset()
    setVoiceOverlayOpen(false)
    setInput('')
    const optimisticId = `user-${Date.now()}`
    const liveAssistantId = `assistant-${Date.now()}`
    pushLive({ id: optimisticId, role: 'user', text })
    pushLive({
      id: liveAssistantId,
      role: 'assistant',
      html: '',
      text: '',
      agentName: selectedAgent?.name,
      parts: null,
    })

    setStreaming(true)
    let newThreadId: string | null = null

    let liveSegments: ChatMessagePart[] = []
    let liveCitations: ChatCitation[] = []

    const syncLive = () => {
      const segmentsSnapshot = liveSegments
      const citationsSnapshot = liveCitations
      const textOnly = segmentsSnapshot
        .filter((s): s is Extract<ChatMessagePart, { kind: 'text' }> => s.kind === 'text')
        .map((s) => s.text)
        .join('\n\n')
      setLiveMessages((prev) =>
        prev.map((m) =>
          m.id === liveAssistantId && m.role === 'assistant'
            ? { ...m, parts: segmentsSnapshot, citations: citationsSnapshot, text: textOnly, html: '' }
            : m,
        ),
      )
    }

    const appendTextDelta = (delta: string) => {
      if (!delta) return
      const last = liveSegments[liveSegments.length - 1]
      if (last && last.kind === 'text') {
        const updated: ChatMessagePart = { kind: 'text', text: last.text + delta }
        liveSegments = [...liveSegments.slice(0, -1), updated]
      } else {
        liveSegments = [...liveSegments, { kind: 'text', text: delta }]
      }
      syncLive()
    }
    const appendParts = (more: ChatMessagePart[]) => {
      if (!more.length) return
      liveSegments = [...liveSegments, ...more]
      syncLive()
    }
    const appendCitations = (more: ChatCitation[]) => {
      if (!more.length) return
      liveCitations = [...liveCitations, ...more]
      syncLive()
    }

    // For brand-new chats, forward the queued visibility so the auto-
    // created thread is born shared/workspace if the user picked that
    // from the + dropdown. For continuing chats we pass nothing - the
    // server ignores visibility when thread_id is set anyway.
    const visibilityPayload = !activeThreadId && pendingVisibility
      ? {
          visibility: pendingVisibility.visibility,
          shared_role_ids: pendingVisibility.sharedRoleIds,
          shared_user_ids: pendingVisibility.sharedUserIds,
        }
      : {}

    try {
      await streamChat(
        {
          message: text,
          agent_id: selectedAgent?.id,
          thread_id: activeThreadId ?? undefined,
          ...visibilityPayload,
        },
        (evt) => {
          if (evt.type === 'meta') {
            if (!activeThreadId) newThreadId = evt.thread_id
            setLiveMessages((prev) =>
              prev.map((m) =>
                m.id === optimisticId && m.role === 'user'
                  ? { ...m, messageId: evt.user_message_id }
                  : m,
              ),
            )
          } else if (evt.type === 'text') {
            appendTextDelta(evt.delta)
          } else if (evt.type === 'tool_result' && evt.ok) {
            if (evt.parts?.length) appendParts(evt.parts)
            if (evt.citations?.length) appendCitations(evt.citations)
          } else if (evt.type === 'done') {
            if (evt.parts?.length) {
              liveSegments = evt.parts
              syncLive()
            }
          } else if (evt.type === 'persisted') {
            setLiveMessages((prev) =>
              prev.map((m) =>
                m.id === liveAssistantId && m.role === 'assistant'
                  ? { ...m, messageId: evt.message_id }
                  : m,
              ),
            )
          } else if (evt.type === 'error') {
            appendTextDelta(`\n\n${evt.message}`)
          }
        },
      )
      // If this stream opened a new thread, pin the client to it without
      // wiping the live buffer. The activeThreadId effect above clears live
      // state on every change; flipping skipClearOnNextThreadChange tells it
      // "this transition came from us, leave the buffer alone." The dedup in
      // `messages` drops the live rows once the persisted query returns.
      if (newThreadId) {
        skipClearOnNextThreadChange.current = true
        setActiveThreadId(newThreadId)
      }
      // The thread is now real - drop the queued visibility so the next
      // "+ New" defaults back to private (server-side default) unless the
      // user picks again.
      if (pendingVisibility) setPendingVisibility(null)
      // Debounce sidebar refresh so the thread-list order doesn't flip
      // mid-stream when updated_at bumps.
      setTimeout(() => {
        qc.invalidateQueries({ queryKey: ['tenant', tenantId, 'chat', 'threads'] })
      }, 600)
    } catch {
      setLiveMessages((prev) =>
        prev.map((m) =>
          m.id === liveAssistantId && m.role === 'assistant'
            ? { ...m, html: sanitize(renderChatMarkdown('Network error. Please check your connection.')) }
            : m,
        ),
      )
    } finally {
      setStreaming(false)
    }
  }

  const onInputKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      e.currentTarget.form?.requestSubmit()
    }
  }

  const sendFeedback = async (messageId: string, rating: 1 | -1) => {
    const current = feedbackLocal[messageId]
    // Toggle off if the user taps the already-selected side.
    const nextRating: 1 | -1 | 0 = current === rating ? 0 : rating
    const prev = current
    setFeedbackLocal((p) => ({ ...p, [messageId]: nextRating }))
    try {
      await feedbackM.mutateAsync({ messageId, rating: nextRating })
    } catch {
      setFeedbackLocal((p) => {
        const copy = { ...p }
        if (prev === undefined) delete copy[messageId]
        else copy[messageId] = prev
        return copy
      })
    }
  }

  const activePill = selectedAgent ? (
    <div className="mb-2 inline-flex w-fit items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
      <Sparkles className="size-3" />
      {selectedAgent.name}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={clearAgent}
        className="size-5 rounded-full text-primary hover:bg-primary/20 hover:text-primary"
        aria-label="Clear selected agent"
      >
        <X className="size-3" />
      </Button>
    </div>
  ) : null

  const inputBar = (
    <form onSubmit={onSubmit} className="relative w-full">
      {activePill}
      {voiceOverlayOpen ? (
        // Floats above the input bar so opening/closing it doesn't push
        // surrounding layout (the greeting stays centered, the messages
        // list doesn't shift).
        <div className="absolute inset-x-0 bottom-full z-20 mb-2">
          <VoiceRecorderOverlay
            listening={voiceInput.listening}
            speaking={voiceInput.speaking}
            transcript={voiceInput.transcript}
            interim={voiceInput.interim}
            error={voiceInput.error}
            onStop={stopVoice}
            onResume={resumeVoice}
            onRestart={restartVoice}
            onCancel={cancelVoice}
            onUse={useVoiceText}
          />
        </div>
      ) : null}
      <div className="flex items-center gap-2 rounded-full border border-input bg-card px-2 py-1.5 shadow-sm focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
        <Popover open={toolsOpen} onOpenChange={setToolsOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-9 rounded-full"
              onClick={openTools}
              aria-label="Open tools"
              title="Agents & workflows"
            >
              <Plus className="size-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" side="top" className="w-[320px] max-w-[90vw] p-0">
            <div className="flex border-b border-border">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setToolsTab('agents')}
                className={cn(
                  'h-auto flex-1 rounded-none border-b-2 px-3 py-2.5 text-xs font-semibold uppercase tracking-wide transition-colors',
                  toolsTab === 'agents'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                AI Agents
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setToolsTab('workflows')}
                className={cn(
                  'h-auto flex-1 rounded-none border-b-2 px-3 py-2.5 text-xs font-semibold uppercase tracking-wide transition-colors',
                  toolsTab === 'workflows'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                Workflows
              </Button>
            </div>
            <div className="border-b border-border p-3">
              <Input
                value={toolsQuery}
                onChange={(e) => setToolsQuery(e.currentTarget.value)}
                placeholder="Search…"
                className="h-8 text-xs"
                autoFocus
              />
            </div>
            <div className="max-h-[320px] overflow-y-auto py-1">
              {toolsTab === 'agents' ? (
                filteredAgents.length === 0 ? (
                  <div className="p-5 text-center text-xs text-muted-foreground">
                    No agents match.
                  </div>
                ) : (
                  filteredAgents.map((a) => {
                    const isSelected = selectedAgent?.id === a.id
                    return (
                      <Button
                        type="button"
                        key={a.id}
                        variant="ghost"
                        onClick={() => chooseAgent(a)}
                        className={cn(
                          'flex h-auto w-full items-center justify-start gap-3 rounded-none px-4 py-2 text-left text-sm transition-colors hover:bg-accent',
                          isSelected && 'bg-primary/10',
                        )}
                      >
                        <span className="flex size-7 shrink-0 items-center justify-center rounded-sm bg-primary/15 text-[10px] font-bold text-primary">
                          {initialsFor(a.name)}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-semibold text-foreground">
                            {a.name}
                          </span>
                          <span className="block truncate text-[11px] font-normal text-muted-foreground">
                            {a.description ?? 'No description'}
                          </span>
                        </span>
                      </Button>
                    )
                  })
                )
              ) : filteredWorkflows.length === 0 ? (
                <div className="p-5 text-center text-xs text-muted-foreground">
                  No workflows match.
                </div>
              ) : (
                filteredWorkflows.map((w) => (
                  <div
                    key={w.id}
                    className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-accent"
                  >
                    <span className="flex size-7 shrink-0 items-center justify-center rounded-sm bg-primary/15 text-[10px] font-bold text-primary">
                      WF
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-semibold text-foreground">
                        {w.name}
                      </span>
                      <span className="block truncate text-[11px] text-muted-foreground">
                        {w.description ?? 'Scheduled workflow'}
                      </span>
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => runWorkflow(w)}
                      disabled={workflowAction.isPending}
                    >
                      Run
                    </Button>
                  </div>
                ))
              )}
            </div>
            <div className="border-t border-border px-3 py-2 text-[11px] text-muted-foreground">
              {toolsTab === 'agents' ? (
                <>
                  Pick an agent to shape how Smart Chat answers.{' '}
                  <Link to="/agents" className="font-semibold text-primary hover:underline">
                    Manage agents →
                  </Link>
                </>
              ) : (
                <>
                  Trigger a workflow now.{' '}
                  <Link to="/workflows" className="font-semibold text-primary hover:underline">
                    Manage workflows →
                  </Link>
                </>
              )}
            </div>
          </PopoverContent>
        </Popover>
        <Input
          value={input}
          onChange={(e) => setInput(e.currentTarget.value)}
          onKeyDown={onInputKey}
          placeholder={voiceOverlayOpen ? 'Recording - review the transcript above' : 'Ask anything'}
          className="h-9 flex-1 border-none bg-transparent px-1 text-sm shadow-none focus-visible:ring-0"
          autoComplete="off"
        />
        {voiceInput.supported ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={cn(
              'size-9 rounded-full',
              voiceOverlayOpen && 'bg-destructive/10 text-destructive hover:bg-destructive/15',
            )}
            onClick={voiceOverlayOpen ? cancelVoice : openVoiceOverlay}
            disabled={streaming}
            aria-label={voiceOverlayOpen ? 'Close voice input' : 'Start voice input'}
            title={voiceInput.error || (voiceOverlayOpen ? 'Close voice input' : 'Voice input')}
          >
            <Mic className="size-4" />
          </Button>
        ) : null}
        <Button
          type="submit"
          size="icon"
          className="size-9 rounded-full"
          disabled={!input.trim() || streaming}
          aria-label="Send message"
        >
          {streaming ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
        </Button>
      </div>
    </form>
  )

  // Single container shape for both greeting and chat views so switching
  // between them doesn't reflow the whole page. The chat surface stays in
  // place; only the inner slot changes.
  //
  // Outer container is a card (white surface + soft 3D shadow + rounded
  // corners) that fills the canonical content area painted by AppShell's
  // <main>. No per-page padding here - AppShell owns the gap from the
  // sidebar so this card lines up with every other page's content.
  return (
    <div className="w-full max-w-screen-2xl">
      <div className="flex h-[calc(100vh-7rem)] w-full overflow-hidden rounded-xl bg-card shadow-card">
      {sidebarCollapsed ? null : (
        <div className="hidden md:block">
          <ThreadSidebar
            activeThreadId={activeThreadId}
            onSelect={setActiveThreadId}
            onToggleCollapse={toggleSidebar}
            pendingVisibility={pendingVisibility}
            onPendingVisibilityChange={setPendingVisibility}
          />
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <ChatHeader
          threadId={activeThreadId}
          onAfterDelete={() => setActiveThreadId(null)}
          sidebarCollapsed={sidebarCollapsed}
          onToggleSidebar={toggleSidebar}
        />
        <div
          className="flex w-full flex-1 flex-col overflow-hidden"
          style={{ contain: 'layout' }}
        >
          {started ? (
            <div className="flex flex-1 flex-col overflow-y-auto">
              <div
                className={cn(
                  'mx-auto flex w-full flex-1 flex-col gap-4 px-4 py-6 sm:px-6',
                  sidebarCollapsed ? 'max-w-6xl' : 'max-w-5xl',
                )}
              >
              {messages.map((m) =>
                m.role === 'user' ? (
                  <div key={m.id} className="flex justify-end">
                    <div className="max-w-[72%] rounded-2xl bg-muted px-4 py-2 text-sm leading-relaxed text-foreground">
                      {m.text}
                    </div>
                  </div>
                ) : m.role === 'status' ? (
                  <div key={m.id} className="text-center text-xs text-muted-foreground">
                    {m.text}
                  </div>
                ) : (
                  <div key={m.id} className="flex items-start">
                    <div className="min-w-0 flex-1 pt-0.5 text-sm leading-relaxed text-foreground">
                      {m.agentName ? (
                        <div className="mb-2 inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 text-[11px] font-semibold text-primary">
                          <Sparkles className="size-3" />
                          {m.agentName}
                        </div>
                      ) : null}
                      {hasTextSegments(m.parts) ? (
                        <div
                          className={cn(
                            'chat-prose',
                            speech.supported && m.text ? 'cursor-pointer' : undefined,
                            speech.speakingId === m.id && 'bk-speaking-ring rounded-lg',
                          )}
                          role={speech.supported && m.text ? 'button' : undefined}
                          title={speech.supported && m.text ? 'Click to read aloud' : undefined}
                          onClick={(e) => {
                            if (!speech.supported || !m.text) return
                            const target = e.target as HTMLElement
                            if (target.closest('a, button, input, select, textarea')) return
                            const sel = window.getSelection()
                            if (sel && sel.toString().length > 0) return
                            if (speech.speakingId === m.id) speech.stop()
                            else speech.speak(m.id, m.text)
                          }}
                        >
                          <PartsRenderer
                            parts={m.parts}
                            messageId={m.messageId}
                            threadId={activeThreadId}
                          />
                        </div>
                      ) : (
                        <>
                          <div
                            className={cn(
                              'chat-prose',
                              speech.supported && m.text ? 'cursor-pointer' : undefined,
                              speech.speakingId === m.id && 'bk-speaking-ring rounded-lg',
                            )}
                            role={speech.supported && m.text ? 'button' : undefined}
                            title={speech.supported && m.text ? 'Click to read aloud' : undefined}
                            onClick={(e) => {
                              if (!speech.supported || !m.text) return
                              const target = e.target as HTMLElement
                              if (target.closest('a, button, input, select, textarea')) return
                              const sel = window.getSelection()
                              if (sel && sel.toString().length > 0) return
                              if (speech.speakingId === m.id) speech.stop()
                              else speech.speak(m.id, m.text)
                            }}
                            // eslint-disable-next-line react/no-danger -- sanitized via DOMPurify in lib/html.ts
                            dangerouslySetInnerHTML={{ __html: m.html }}
                          />
                          <PartsRenderer
                            parts={m.parts}
                            messageId={m.messageId}
                            threadId={activeThreadId}
                          />
                        </>
                      )}
                      <CitationList
                        citations={m.citations}
                        threadId={activeThreadId}
                        messageId={m.messageId}
                      />
                      {m.messageId ? (
                        <div className="mt-2 flex items-center gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className={cn(
                              'size-7 rounded-full text-muted-foreground transition-colors hover:text-foreground',
                              feedbackLocal[m.messageId] === 1 &&
                                'bg-primary/10 text-primary hover:bg-primary/15 hover:text-primary',
                            )}
                            onClick={() => sendFeedback(m.messageId!, 1)}
                            aria-label={
                              feedbackLocal[m.messageId] === 1
                                ? 'Clear good-response rating'
                                : 'Good response'
                            }
                            title={
                              feedbackLocal[m.messageId] === 1
                                ? 'Click again to clear'
                                : 'Good response'
                            }
                          >
                            <ThumbsUp className="size-3.5" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className={cn(
                              'size-7 rounded-full text-muted-foreground transition-colors hover:text-destructive',
                              feedbackLocal[m.messageId] === -1 &&
                                'bg-destructive/10 text-destructive hover:bg-destructive/15 hover:text-destructive',
                            )}
                            onClick={() => sendFeedback(m.messageId!, -1)}
                            aria-label={
                              feedbackLocal[m.messageId] === -1
                                ? 'Clear bad-response rating'
                                : 'Bad response'
                            }
                            title={
                              feedbackLocal[m.messageId] === -1
                                ? 'Click again to clear'
                                : 'Bad response'
                            }
                          >
                            <ThumbsDown className="size-3.5" />
                          </Button>
                          {speech.supported && m.text ? (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className={cn(
                                'size-7 rounded-full text-muted-foreground hover:text-foreground',
                                speech.speakingId === m.id && 'bg-primary/10 text-primary hover:bg-primary/15 hover:text-primary',
                              )}
                              onClick={() =>
                                speech.speakingId === m.id
                                  ? speech.stop()
                                  : speech.speak(m.id, m.text || '')
                              }
                              aria-label={
                                speech.speakingId === m.id ? 'Stop speaking' : 'Read aloud'
                              }
                              title={
                                speech.speakingId === m.id ? 'Stop speaking' : 'Read aloud'
                              }
                            >
                              {speech.speakingId === m.id ? (
                                <SpeakingWaves className="size-3.5" />
                              ) : (
                                <Volume2 className="size-3.5" />
                              )}
                            </Button>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ),
              )}
              {streaming ? (
                <div className="flex items-center gap-1">
                  <span className="size-2 animate-bounce rounded-full bg-muted-foreground [animation-delay:-0.32s]" />
                  <span className="size-2 animate-bounce rounded-full bg-muted-foreground [animation-delay:-0.16s]" />
                  <span className="size-2 animate-bounce rounded-full bg-muted-foreground" />
                </div>
              ) : null}
              <div ref={messagesEndRef} />
              </div>
            </div>
          ) : (
            // First-time empty state: greeting + input share the centered
            // block so the user lands on a single focal point. Reserve some
            // bottom padding so flex `justify-center` settles the block in
            // the upper third rather than the geometric middle.
            <div className="flex flex-1 flex-col items-center justify-center px-4 pb-[18vh] text-center">
              <h1 className="mb-1 text-2xl font-semibold tracking-tight sm:text-3xl">{greeting}</h1>
              <p className="text-sm text-muted-foreground sm:text-base">Happy to help!</p>
              <div
                className={cn(
                  'mt-4 w-full px-2 sm:px-0',
                  sidebarCollapsed ? 'max-w-3xl' : 'max-w-2xl',
                )}
              >
                {inputBar}
              </div>
            </div>
          )}
          {started ? (
            <div
              className={cn(
                'mx-auto w-full px-4 pb-4 sm:px-6',
                sidebarCollapsed ? 'max-w-6xl' : 'max-w-5xl',
              )}
            >
              {inputBar}
            </div>
          ) : null}
        </div>
      </div>
      </div>
    </div>
  )
}
